import React, { useCallback, useEffect, useRef, useState } from "react";
import useApi from "@/hooks/useApi";

// ── Types ─────────────────────────────────────────────────────────────────────

interface LogEntry {
  id: string;
  timestamp: string;
  level: string | null;
  logger: string | null;
  message: string | null;
  environment: string | null;
  siteId: number | null;
  integrationTag: string | null;
  source: string | null;
}

type FilterField = "message" | "logger" | "level" | "siteId" | "integrationTag" | "source";
type FilterOp = "contains" | "equals" | "startsWith" | "notContains";

interface FilterRow {
  id: string;
  field: FilterField;
  op: FilterOp;
  value: string;
}

interface SearchState {
  q: string;
  level: string;
  loggerFilter: string;
  siteId: string;
  integration: string;
  source: string;
  from: string;
  to: string;
  take: number;
  filterRows: FilterRow[];
}

interface SavedSearch {
  id: string;
  name: string;
  filtersJson: string;
  createdAt: string;
}

interface AlertRule {
  id?: number;
  name: string;
  queryText?: string;
  level?: string;
  integration?: string;
  source?: string;
  logger?: string;
  siteId?: number;
  windowMinutes: number;
  thresholdCount: number;
  notifyPhoneNumbers?: string;
  notifyWebhookUrl?: string;
  notifyEmails?: string;
  notifyMobileUserIds?: string;
  minRefireMinutes: number;
  enabled: boolean;
  lastFiredAt?: string;
  createdAt?: string;
}

interface AlertHistoryEntry {
  ruleId: number;
  ruleName: string;
  matchCount: number;
  threshold: number;
  windowMinutes: number;
  message: string;
  firedAt: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const FIELD_LABELS: Record<FilterField, string> = {
  message: "Message", logger: "Logger", level: "Level",
  siteId: "Site ID", integrationTag: "Integration tag", source: "Source app"
};
const OP_LABELS: Record<FilterOp, string> = {
  contains: "contains", notContains: "does not contain",
  equals: "equals", startsWith: "starts with"
};
const LEVELS = ["ERROR", "WARN", "INFO", "DEBUG"];
const LEVEL_COLORS: Record<string, string> = {
  ERROR: "bg-red-100 text-red-700 border-red-300",
  WARN:  "bg-yellow-100 text-yellow-700 border-yellow-300",
  INFO:  "bg-blue-100 text-blue-700 border-blue-300",
  DEBUG: "bg-slate-100 text-slate-500 border-slate-300",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

let filterIdCounter = 0;
const newRow = (): FilterRow => ({ id: String(++filterIdCounter), field: "message", op: "contains", value: "" });

function defaultFrom() {
  const d = new Date(); d.setHours(d.getHours() - 2); return d.toISOString().slice(0, 16);
}

function levelBadge(level: string | null) {
  const l = (level ?? "").toUpperCase();
  return <span className={`inline-block text-xs font-mono font-semibold px-1.5 py-0.5 rounded border ${LEVEL_COLORS[l] ?? "bg-slate-100 text-slate-500 border-slate-300"}`}>{l || "?"}</span>;
}

function shortLogger(logger: string | null) {
  if (!logger) return "";
  const parts = logger.split(".");
  return parts[parts.length - 1] ?? logger;
}

function highlightTags(message: string | null): React.ReactNode {
  if (!message) return null;
  return message.split(/(\[(?:Skill|HttpNode)\])/gi).map((p, i) =>
    /^\[(?:Skill|HttpNode)\]$/i.test(p)
      ? <span key={i} className="bg-purple-100 text-purple-700 rounded px-1 font-semibold text-xs">{p}</span>
      : p
  );
}

function applyExpressionFilters(entries: LogEntry[], rows: FilterRow[]): LogEntry[] {
  const active = rows.filter(r => r.value.trim() !== "");
  if (!active.length) return entries;
  return entries.filter(e => active.every(f => {
    const hay = (() => {
      switch (f.field) {
        case "message": return e.message ?? "";
        case "logger": return e.logger ?? "";
        case "level": return e.level ?? "";
        case "siteId": return String(e.siteId ?? "");
        case "integrationTag": return e.integrationTag ?? "";
        case "source": return e.source ?? "";
      }
    })().toLowerCase();
    const val = f.value.toLowerCase();
    switch (f.op) {
      case "contains": return hay.includes(val);
      case "notContains": return !hay.includes(val);
      case "equals": return hay === val;
      case "startsWith": return hay.startsWith(val);
    }
  }));
}

function exportCsv(entries: LogEntry[]) {
  const header = "id,timestamp,level,logger,siteId,integrationTag,message\n";
  const rows = entries.map(e => [e.id, e.timestamp, e.level ?? "", e.logger ?? "", e.siteId ?? "", e.integrationTag ?? "", `"${(e.message ?? "").replace(/"/g, '""')}"`].join(",")).join("\n");
  const blob = new Blob([header + rows], { type: "text/csv" });
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `velaro-logs-${new Date().toISOString().slice(0, 16)}.csv` });
  a.click(); URL.revokeObjectURL(a.href);
}

// ── Blank alert rule ──────────────────────────────────────────────────────────

const BLANK_RULE: AlertRule = {
  name: "", windowMinutes: 5, thresholdCount: 1,
  minRefireMinutes: 60, enabled: true
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function VelaroLogger() {
  const api = useApi();

  // Search state
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [level, setLevel] = useState("");
  const [loggerFilter, setLoggerFilter] = useState("");
  const [loggerInput, setLoggerInput] = useState("");
  const [siteId, setSiteId] = useState("");
  const [integration, setIntegration] = useState("");
  const [source, setSource] = useState("");
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 16));
  const [take, setTake] = useState(200);
  const [availableIntegrations, setAvailableIntegrations] = useState<string[]>([]);
  const [availableSources, setAvailableSources] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Expression builder
  const [showBuilder, setShowBuilder] = useState(false);
  const [filterRows, setFilterRows] = useState<FilterRow[]>([newRow()]);

  // Active panel
  const [activePanel, setActivePanel] = useState<"logs" | "ai" | "saved" | "alerts" | "history">("logs");

  // AI analysis
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  // Saved searches
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [savedSearchName, setSavedSearchName] = useState("");
  const [savingSearch, setSavingSearch] = useState(false);

  // Alert rules
  const [alertRules, setAlertRules] = useState<AlertRule[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [savingRule, setSavingRule] = useState(false);
  const [alertHistory, setAlertHistory] = useState<AlertHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const visibleEntries = applyExpressionFilters(entries, filterRows);

  // ── Fetch logs ──

  const fetchFilters = useCallback(async () => {
    try {
      const data = await (await api.messaging.get("superadmin/logs/filters")).json();
      setAvailableIntegrations(Array.isArray(data.integrations) ? data.integrations : []);
      setAvailableSources(Array.isArray(data.sources) ? data.sources : []);
    } catch { setAvailableIntegrations([]); setAvailableSources([]); }
  }, [api]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ take: String(take) });
      if (q) params.set("q", q);
      if (level) params.set("level", level);
      if (loggerFilter) params.set("logger", loggerFilter);
      if (siteId) params.set("siteId", siteId);
      if (integration) params.set("integration", integration);
      if (source) params.set("source", source);
      if (from) params.set("from", new Date(from).toISOString());
      if (to) params.set("to", new Date(to).toISOString());
      const data = await (await api.messaging.get(`superadmin/logs/search?${params}`)).json();
      setEntries(Array.isArray(data) ? data : []);
    } catch { setEntries([]); }
    finally { setLoading(false); }
  }, [api, q, level, loggerFilter, siteId, integration, source, from, to, take]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => {
        setTo(new Date().toISOString().slice(0, 16));
        fetchLogs();
      }, 5000);
    }
    return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current); };
  }, [autoRefresh, fetchLogs]);

  // ── Saved searches ──

  const loadSavedSearches = useCallback(async () => {
    try {
      const data = await (await api.messaging.get("superadmin/log-alerts/saved-searches")).json();
      setSavedSearches(Array.isArray(data) ? data : []);
    } catch { setSavedSearches([]); }
  }, [api]);

  useEffect(() => { if (activePanel === "saved") loadSavedSearches(); }, [activePanel, loadSavedSearches]);

  function captureCurrentState(): SearchState {
    return { q, level, loggerFilter, siteId, integration, source, from, to, take, filterRows };
  }

  function applySearchState(state: SearchState) {
    setQ(state.q ?? ""); setQInput(state.q ?? "");
    setLevel(state.level ?? "");
    setLoggerFilter(state.loggerFilter ?? ""); setLoggerInput(state.loggerFilter ?? "");
    setSiteId(state.siteId ?? "");
    setIntegration(state.integration ?? "");
    setSource(state.source ?? "");
    if (state.from) setFrom(state.from);
    if (state.to) setTo(state.to);
    if (state.take) setTake(state.take);
    if (Array.isArray(state.filterRows)) setFilterRows(state.filterRows.length ? state.filterRows : [newRow()]);
    setActivePanel("logs");
  }

  async function handleSaveSearch() {
    if (!savedSearchName.trim()) return;
    setSavingSearch(true);
    try {
      await api.messaging.post("superadmin/log-alerts/saved-searches", {
        name: savedSearchName.trim(),
        filtersJson: JSON.stringify(captureCurrentState())
      });
      setSavedSearchName("");
      await loadSavedSearches();
    } catch { /* ignore */ }
    finally { setSavingSearch(false); }
  }

  async function handleDeleteSavedSearch(id: string) {
    await api.messaging.delete(`superadmin/log-alerts/saved-searches/${id}`);
    setSavedSearches(prev => prev.filter(s => s.id !== id));
  }

  function handleLoadSearch(s: SavedSearch) {
    try {
      const state: SearchState = JSON.parse(s.filtersJson);
      applySearchState(state);
    } catch { /* ignore parse errors */ }
  }

  // ── Alert rules ──

  const loadAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const data = await (await api.messaging.get("superadmin/log-alerts/rules")).json();
      setAlertRules(Array.isArray(data) ? data : []);
    } catch { setAlertRules([]); }
    finally { setAlertsLoading(false); }
  }, [api]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await (await api.messaging.get("superadmin/log-alerts/history?take=50")).json();
      setAlertHistory(Array.isArray(data) ? data : []);
    } catch { setAlertHistory([]); }
    finally { setHistoryLoading(false); }
  }, [api]);

  useEffect(() => {
    if (activePanel === "alerts") loadAlerts();
    if (activePanel === "history") loadHistory();
  }, [activePanel, loadAlerts, loadHistory]);

  async function handleSaveRule() {
    if (!editingRule?.name?.trim()) return;
    setSavingRule(true);
    try {
      if (editingRule.id) {
        const data = await (await api.messaging.put(`superadmin/log-alerts/rules/${editingRule.id}`, editingRule)).json();
        setAlertRules(prev => prev.map(r => r.id === editingRule.id ? data : r));
      } else {
        const data = await (await api.messaging.post("superadmin/log-alerts/rules", editingRule)).json();
        setAlertRules(prev => [data, ...prev]);
      }
      setEditingRule(null);
    } catch { /* ignore */ }
    finally { setSavingRule(false); }
  }

  async function handleDeleteRule(id: number) {
    await api.messaging.delete(`superadmin/log-alerts/rules/${id}`);
    setAlertRules(prev => prev.filter(r => r.id !== id));
  }

  async function handleToggleRule(id: number) {
    const data = await (await api.messaging.post(`superadmin/log-alerts/rules/${id}/toggle`, {})).json();
    setAlertRules(prev => prev.map(r => r.id === id ? { ...r, enabled: data.enabled } : r));
  }

  // ── Search handlers ──

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setQ(qInput);
    setLoggerFilter(loggerInput);
  }

  function handleClear() {
    setQ(""); setQInput(""); setLoggerFilter(""); setLoggerInput("");
    setLevel(""); setSiteId(""); setIntegration(""); setSource("");
    setFilterRows([newRow()]);
  }

  // ── AI analysis ──

  async function handleAiAnalyze() {
    if (!visibleEntries.length) return;
    setAiLoading(true); setAiAnswer("");
    try {
      const data = await (await api.messaging.post("superadmin/logs/analyze", {
        question: aiQuestion || undefined,
        entries: visibleEntries.slice(0, 150).map(e => ({
          timestamp: e.timestamp, level: e.level, logger: e.logger, message: e.message, siteId: e.siteId
        }))
      })).json();
      setAiAnswer(data.answer ?? "No response.");
    } catch (ex: any) { setAiAnswer("Analysis failed: " + ex.message); }
    finally { setAiLoading(false); }
  }

  // ── Quick filters ──

  const QUICK_FILTERS = [
    { label: "Errors only", apply: () => setLevel("ERROR") },
    { label: "Skills", apply: () => setIntegration("[Skill]") },
    { label: "HTTP nodes", apply: () => setIntegration("[HttpNode]") },
    { label: "Last 30 min", apply: () => { const d = new Date(); d.setMinutes(d.getMinutes() - 30); setFrom(d.toISOString().slice(0, 16)); setTo(new Date().toISOString().slice(0, 16)); } },
    { label: "Last 6 hr", apply: () => { const d = new Date(); d.setHours(d.getHours() - 6); setFrom(d.toISOString().slice(0, 16)); setTo(new Date().toISOString().slice(0, 16)); } },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 max-w-7xl">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <p className="text-sm text-slate-500">
            Self-hosted log search — <code className="text-xs bg-slate-100 px-1 rounded">velaro-messaging-api-staging</code>
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            {visibleEntries.length}{visibleEntries.length !== entries.length ? ` of ${entries.length}` : ""} result{visibleEntries.length !== 1 ? "s" : ""}
            {loading && <span className="ml-2 text-blue-500">Loading…</span>}
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button onClick={() => exportCsv(visibleEntries)} disabled={!visibleEntries.length}
            className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-40">
            Export CSV
          </button>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} className="rounded" />
            Live tail (5s)
          </label>
          <button onClick={fetchLogs} className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700">
            Refresh
          </button>
        </div>
      </div>

      {/* Quick filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        {QUICK_FILTERS.map(f => (
          <button key={f.label} onClick={() => { f.apply(); setTimeout(fetchLogs, 0); }}
            className="text-xs px-2.5 py-1 border border-slate-200 rounded-full hover:bg-slate-100 text-slate-600">
            {f.label}
          </button>
        ))}
      </div>

      {/* Main filter form */}
      <form onSubmit={handleSearch} className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Search message</label>
          <input type="text" className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="[Skill], NetSuite, error text…" value={qInput} onChange={e => setQInput(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Logger</label>
          <input type="text" className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="e.g. NetSuiteSkill" value={loggerInput} onChange={e => setLoggerInput(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Level</label>
          <select className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={level} onChange={e => setLevel(e.target.value)}>
            <option value="">All levels</option>
            {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Integration tag</label>
          <select className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={integration} onChange={e => setIntegration(e.target.value)}>
            <option value="">All integrations</option>
            {availableIntegrations.map(i => <option key={i} value={i}>{i}</option>)}
            {!availableIntegrations.includes("[Skill]") && <option value="[Skill]">[Skill]</option>}
            {!availableIntegrations.includes("[HttpNode]") && <option value="[HttpNode]">[HttpNode]</option>}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Source app</label>
          <select className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={source} onChange={e => setSource(e.target.value)}>
            <option value="">All sources</option>
            {availableSources.map(s => <option key={s} value={s}>{s}</option>)}
            {!availableSources.length && <>
              <option value="velaro-messaging-Staging">velaro-messaging-Staging</option>
              <option value="velaro-messaging-Production">velaro-messaging-Production</option>
              <option value="livefluence-Staging">livefluence-Staging</option>
              <option value="livefluence-Production">livefluence-Production</option>
            </>}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Site ID</label>
          <input type="text" className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="e.g. 1234" value={siteId} onChange={e => setSiteId(e.target.value.replace(/\D/g, ""))} />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">From</label>
          <input type="datetime-local" className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">To</label>
          <input type="datetime-local" className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Max rows</label>
          <select className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={take} onChange={e => setTake(Number(e.target.value))}>
            {[50, 100, 200, 500].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="col-span-2 lg:col-span-4 flex gap-2 justify-between items-end">
          <button type="button" onClick={() => setShowBuilder(v => !v)}
            className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50 text-slate-600">
            {showBuilder ? "Hide" : "Expression builder"}
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={handleClear}
              className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50">Clear</button>
            <button type="submit" className="text-xs px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700">Search</button>
          </div>
        </div>
      </form>

      {/* Expression builder */}
      {showBuilder && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-3">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
            Expression filters (AND) — applied client-side after fetch
          </div>
          <div className="space-y-2">
            {filterRows.map((row, idx) => (
              <div key={row.id} className="flex gap-2 items-center">
                <select value={row.field}
                  onChange={e => setFilterRows(prev => prev.map(r => r.id === row.id ? { ...r, field: e.target.value as FilterField } : r))}
                  className="text-xs border border-slate-300 rounded px-2 py-1.5">
                  {(Object.keys(FIELD_LABELS) as FilterField[]).map(f => <option key={f} value={f}>{FIELD_LABELS[f]}</option>)}
                </select>
                <select value={row.op}
                  onChange={e => setFilterRows(prev => prev.map(r => r.id === row.id ? { ...r, op: e.target.value as FilterOp } : r))}
                  className="text-xs border border-slate-300 rounded px-2 py-1.5">
                  {(Object.keys(OP_LABELS) as FilterOp[]).map(op => <option key={op} value={op}>{OP_LABELS[op]}</option>)}
                </select>
                <input type="text" value={row.value}
                  onChange={e => setFilterRows(prev => prev.map(r => r.id === row.id ? { ...r, value: e.target.value } : r))}
                  placeholder="value…"
                  className="flex-1 text-xs border border-slate-300 rounded px-2 py-1.5" />
                {filterRows.length > 1 && (
                  <button onClick={() => setFilterRows(prev => prev.filter(r => r.id !== row.id))} className="text-slate-400 hover:text-red-500 text-sm px-1">✕</button>
                )}
                {idx === filterRows.length - 1 && (
                  <button onClick={() => setFilterRows(prev => [...prev, newRow()])} className="text-xs text-blue-600 hover:underline whitespace-nowrap">+ AND</button>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2">
            All conditions must match. Operates on the {entries.length} fetched rows without a new request.
          </p>
        </div>
      )}

      {/* Panel tabs */}
      <div className="flex gap-1 mb-3 border-b border-slate-200">
        {[
          { id: "logs", label: `Log results (${visibleEntries.length})` },
          { id: "ai", label: "AI analysis" },
          { id: "saved", label: "Saved searches" },
          { id: "alerts", label: `Alert rules${alertRules.filter(r => r.enabled).length ? ` (${alertRules.filter(r => r.enabled).length} active)` : ""}` },
          { id: "history", label: "Alert history" },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActivePanel(tab.id as any)}
            className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
              activePanel === tab.id ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Log results ── */}
      {activePanel === "logs" && (
        <>
          <div className="border border-slate-200 rounded-lg overflow-hidden text-sm">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 w-36">Timestamp</th>
                  <th className="px-3 py-2 w-16">Level</th>
                  <th className="px-3 py-2 w-32">Logger</th>
                  <th className="px-3 py-2 w-20">Site</th>
                  <th className="px-3 py-2 w-24">Tag</th>
                  <th className="px-3 py-2">Message</th>
                </tr>
              </thead>
              <tbody>
                {!visibleEntries.length && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">
                    {loading ? "Loading…" : "No results. Adjust filters or check that VELARO_LOGGER_ENABLED=true in Azure App Settings."}
                  </td></tr>
                )}
                {visibleEntries.map(entry => {
                  const isExpanded = expandedId === entry.id;
                  return (
                    <tr key={entry.id} onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                      className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${entry.level === "ERROR" ? "bg-red-50" : entry.level === "WARN" ? "bg-yellow-50" : ""}`}>
                      <td className="px-3 py-1.5 font-mono text-xs text-slate-500 whitespace-nowrap">{new Date(entry.timestamp).toLocaleString()}</td>
                      <td className="px-3 py-1.5">{levelBadge(entry.level)}</td>
                      <td className="px-3 py-1.5 text-xs text-slate-600 font-mono truncate max-w-[8rem]" title={entry.logger ?? undefined}>{shortLogger(entry.logger)}</td>
                      <td className="px-3 py-1.5 text-xs text-slate-500">{entry.siteId ?? <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-1.5">
                        {entry.integrationTag
                          ? <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 rounded px-1.5 py-0.5 font-medium">{entry.integrationTag}</span>
                          : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-slate-700">
                        {isExpanded
                          ? <div className="whitespace-pre-wrap font-mono bg-slate-900 text-slate-100 rounded p-2 mt-1 text-xs leading-relaxed">{entry.message}</div>
                          : <span className="truncate block max-w-xl" title={entry.message ?? undefined}>{highlightTags(entry.message)}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {visibleEntries.length > 0 && <p className="text-xs text-slate-400 mt-2 text-right">Showing {visibleEntries.length} rows. Click row to expand.</p>}
        </>
      )}

      {/* ── AI Analysis ── */}
      {activePanel === "ai" && (
        <div className="border border-purple-200 rounded-lg bg-purple-50 p-5">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Ask Haiku about the current {visibleEntries.length} log entries
          </label>
          <textarea value={aiQuestion} onChange={e => setAiQuestion(e.target.value)} rows={3}
            placeholder="e.g. What is causing the errors? Which integration is failing? Summarize the root cause."
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white resize-none mb-3" />
          <p className="text-xs text-slate-400 mb-3">Leave blank for a general summary. Sends up to 150 rows to Haiku.</p>
          <div className="flex gap-3 mb-4">
            <button onClick={handleAiAnalyze} disabled={aiLoading || !visibleEntries.length}
              className="px-4 py-2 bg-purple-600 text-white rounded text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {aiLoading ? "Analyzing…" : "Analyze with AI"}
            </button>
            {aiAnswer && <button onClick={() => setAiAnswer("")} className="px-3 py-2 border border-slate-300 rounded text-sm text-slate-500 hover:bg-white">Clear</button>}
          </div>
          {!visibleEntries.length && <div className="text-sm text-slate-400 text-center py-4">No log entries loaded. Run a search first.</div>}
          {aiLoading && <div className="text-sm text-purple-600 animate-pulse">Haiku is reading {Math.min(visibleEntries.length, 150)} log entries…</div>}
          {aiAnswer && !aiLoading && (
            <div className="bg-white border border-purple-200 rounded p-4">
              <div className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-2">Haiku response</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{aiAnswer}</div>
            </div>
          )}
        </div>
      )}

      {/* ── Saved Searches ── */}
      {activePanel === "saved" && (
        <div>
          {/* Save current */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-5">
            <div className="text-sm font-medium text-slate-700 mb-2">Save current search</div>
            <div className="flex gap-2">
              <input type="text" value={savedSearchName} onChange={e => setSavedSearchName(e.target.value)}
                placeholder="Name this search…"
                className="flex-1 border border-slate-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
              <button onClick={handleSaveSearch} disabled={savingSearch || !savedSearchName.trim()}
                className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {savingSearch ? "Saving…" : "Save"}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">Saves all current filter settings including date range, level, integrations, and expression builder rows.</p>
          </div>

          {/* Saved list */}
          {savedSearches.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-8">No saved searches yet.</div>
          ) : (
            <div className="space-y-2">
              {savedSearches.map(s => (
                <div key={s.id} className="flex items-center justify-between bg-white border border-slate-200 rounded px-4 py-2.5">
                  <div>
                    <span className="text-sm font-medium text-slate-800">{s.name}</span>
                    <span className="ml-2 text-xs text-slate-400">{new Date(s.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleLoadSearch(s)}
                      className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">Load</button>
                    <button onClick={() => handleDeleteSavedSearch(s.id)}
                      className="text-xs px-2 py-1 border border-red-200 text-red-500 rounded hover:bg-red-50">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Alert Rules ── */}
      {activePanel === "alerts" && (
        <div>
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
            Alert rules are evaluated every minute by a Hangfire job in velaro-messaging — <strong>alerts fire even if velaro-admin is down</strong>. Circuit breaker: each rule has a minimum re-fire interval.
          </div>

          {/* Rule form */}
          {editingRule && (
            <div className="bg-white border border-slate-200 rounded-lg p-5 mb-5 shadow-sm">
              <div className="text-sm font-semibold text-slate-700 mb-4">{editingRule.id ? "Edit rule" : "New alert rule"}</div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">Rule name *</label>
                  <input type="text" value={editingRule.name}
                    onChange={e => setEditingRule(r => ({ ...r!, name: e.target.value }))}
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>

                <div className="col-span-2">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Filter (what to watch)</div>
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">Message contains</label>
                  <input type="text" value={editingRule.queryText ?? ""}
                    onChange={e => setEditingRule(r => ({ ...r!, queryText: e.target.value || undefined }))}
                    placeholder="e.g. exception, failed, timeout"
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Level</label>
                  <select value={editingRule.level ?? ""}
                    onChange={e => setEditingRule(r => ({ ...r!, level: e.target.value || undefined }))}
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                    <option value="">Any</option>
                    {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Integration tag</label>
                  <input type="text" value={editingRule.integration ?? ""}
                    onChange={e => setEditingRule(r => ({ ...r!, integration: e.target.value || undefined }))}
                    placeholder="e.g. NetSuite, [Skill]"
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Logger contains</label>
                  <input type="text" value={editingRule.logger ?? ""}
                    onChange={e => setEditingRule(r => ({ ...r!, logger: e.target.value || undefined }))}
                    placeholder="e.g. NetSuiteSkill"
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Site ID</label>
                  <input type="number" value={editingRule.siteId ?? ""}
                    onChange={e => setEditingRule(r => ({ ...r!, siteId: e.target.value ? Number(e.target.value) : undefined }))}
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Source app</label>
                  <input type="text" value={editingRule.source ?? ""}
                    onChange={e => setEditingRule(r => ({ ...r!, source: e.target.value || undefined }))}
                    placeholder="e.g. velaro-messaging-Staging"
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>

                <div className="col-span-2">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 mt-1">Threshold</div>
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">Window (minutes)</label>
                  <input type="number" min={1} max={1440} value={editingRule.windowMinutes}
                    onChange={e => setEditingRule(r => ({ ...r!, windowMinutes: Number(e.target.value) }))}
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Fire when count &ge;</label>
                  <input type="number" min={1} value={editingRule.thresholdCount}
                    onChange={e => setEditingRule(r => ({ ...r!, thresholdCount: Number(e.target.value) }))}
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>

                <div className="col-span-2">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 mt-1">Notifications</div>
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">SMS phone numbers (comma-separated)</label>
                  <input type="text" value={editingRule.notifyPhoneNumbers ?? ""}
                    onChange={e => setEditingRule(r => ({ ...r!, notifyPhoneNumbers: e.target.value || undefined }))}
                    placeholder="+14436295872,+12223334444"
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Webhook URL</label>
                  <input type="text" value={editingRule.notifyWebhookUrl ?? ""}
                    onChange={e => setEditingRule(r => ({ ...r!, notifyWebhookUrl: e.target.value || undefined }))}
                    placeholder="https://hooks.slack.com/…"
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Email addresses (comma-separated)</label>
                  <input type="text" value={editingRule.notifyEmails ?? ""}
                    onChange={e => setEditingRule(r => ({ ...r!, notifyEmails: e.target.value || undefined }))}
                    placeholder="noc@velaro.com, alex@velaro.com"
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Business Messenger push — workspace user IDs (comma-separated)</label>
                  <input type="text" value={editingRule.notifyMobileUserIds ?? ""}
                    onChange={e => setEditingRule(r => ({ ...r!, notifyMobileUserIds: e.target.value || undefined }))}
                    placeholder="42, 107"
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  <p className="text-xs text-slate-400 mt-0.5">Only these specific users receive a push on their iOS/Android device.</p>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Circuit breaker: min re-fire (minutes)</label>
                  <input type="number" min={1} value={editingRule.minRefireMinutes}
                    onChange={e => setEditingRule(r => ({ ...r!, minRefireMinutes: Number(e.target.value) }))}
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  <p className="text-xs text-slate-400 mt-0.5">Won't re-fire for this many minutes after last firing.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={handleSaveRule} disabled={savingRule || !editingRule.name?.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                  {savingRule ? "Saving…" : editingRule.id ? "Update rule" : "Create rule"}
                </button>
                <button onClick={() => setEditingRule(null)}
                  className="px-4 py-2 border border-slate-300 rounded text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-slate-600">{alertRules.length} rule{alertRules.length !== 1 ? "s" : ""}</span>
            <button onClick={() => setEditingRule({ ...BLANK_RULE })}
              className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700">+ New rule</button>
          </div>

          {alertsLoading && <div className="text-sm text-slate-400 text-center py-4">Loading…</div>}

          {!alertsLoading && alertRules.length === 0 && (
            <div className="text-sm text-slate-400 text-center py-8 border border-dashed border-slate-200 rounded">
              No alert rules yet. Create one to get SMS, email, webhook, or mobile push notifications when log patterns exceed a threshold.
            </div>
          )}

          {!alertsLoading && alertRules.map(rule => (
            <div key={rule.id} className={`flex items-start justify-between border rounded-lg px-4 py-3 mb-2 ${rule.enabled ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200 opacity-60"}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm text-slate-800">{rule.name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded border font-medium ${rule.enabled ? "bg-green-50 text-green-700 border-green-300" : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                    {rule.enabled ? "active" : "paused"}
                  </span>
                </div>
                <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5">
                  <span>≥{rule.thresholdCount} in {rule.windowMinutes}m</span>
                  {rule.level && <span>level={rule.level}</span>}
                  {rule.queryText && <span>q="{rule.queryText}"</span>}
                  {rule.integration && <span>tag={rule.integration}</span>}
                  {rule.siteId && <span>site={rule.siteId}</span>}
                  {rule.notifyPhoneNumbers && <span>SMS</span>}
                  {rule.notifyEmails && <span>email</span>}
                  {rule.notifyWebhookUrl && <span>webhook</span>}
                  {rule.notifyMobileUserIds && <span>mobile push</span>}
                  <span>cooldown {rule.minRefireMinutes}m</span>
                </div>
                {rule.lastFiredAt && (
                  <div className="text-xs text-amber-600 mt-0.5">Last fired: {new Date(rule.lastFiredAt).toLocaleString()}</div>
                )}
              </div>
              <div className="flex gap-1 ml-3 flex-shrink-0">
                <button onClick={() => handleToggleRule(rule.id!)}
                  className="text-xs px-2.5 py-1 border border-slate-300 rounded hover:bg-slate-50">
                  {rule.enabled ? "Pause" : "Enable"}
                </button>
                <button onClick={() => setEditingRule({ ...rule })}
                  className="text-xs px-2.5 py-1 border border-slate-300 rounded hover:bg-slate-50">Edit</button>
                <button onClick={() => handleDeleteRule(rule.id!)}
                  className="text-xs px-2.5 py-1 border border-red-200 text-red-500 rounded hover:bg-red-50">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Alert History ── */}
      {activePanel === "history" && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-slate-600">Last 50 alert firings</span>
            <button onClick={loadHistory} disabled={historyLoading}
              className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-40">
              {historyLoading ? "Loading…" : "Refresh"}
            </button>
          </div>

          {!historyLoading && alertHistory.length === 0 && (
            <div className="text-sm text-slate-400 text-center py-8">No alerts have fired yet.</div>
          )}

          {alertHistory.map((h, i) => (
            <div key={i} className="border border-amber-200 bg-amber-50 rounded p-3 mb-2">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm text-amber-900">{h.ruleName}</span>
                <span className="text-xs text-amber-600">{new Date(h.firedAt).toLocaleString()}</span>
              </div>
              <div className="text-xs text-amber-700">
                {h.matchCount} matches in {h.windowMinutes}m (threshold {h.threshold})
              </div>
              <div className="text-xs text-amber-600 mt-0.5 font-mono">{h.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
